import { useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useInvitePreview } from '../../lib/queries';
import { SignInPanel } from '../auth/SignInPanel';
import { Splash } from '../../components/Guard';
import { TermsPanel, useNeedsTerms } from '../../components/Legal';
import { useT } from '../../i18n';

export function InviteLanding({ token }: { token: string }) {
  const t = useT().invite;
  const preview = useInvitePreview(token);
  const { user, ready } = useAuth();
  const terms = useNeedsTerms();
  const nav = useNavigate();
  const accept = useMutation({
    mutationFn: () => api<{ treeId: string }>('POST', `/invites/${token}/accept`),
    onSuccess: (r) => void nav({ to: '/t/$treeId', params: { treeId: r.treeId } }),
  });
  if (!ready || preview.isLoading) return <Splash />;
  if (preview.error) {
    return (
      <div className="center">
        <div className="stack" style={{ maxWidth: 380 }}>
          <span className="wordmark">Lineage</span>
          <h1 style={{ fontSize: 30 }}>{t.cantBeUsed}</h1>
          <p className="muted">{(preview.error as ApiError).message}</p>
        </div>
      </div>
    );
  }
  const inv = preview.data!;
  const err = accept.error as ApiError | null;
  return (
    <div className="center">
      <div className="stack" style={{ maxWidth: 380, width: '100%' }}>
        <span className="wordmark">Lineage</span>
        <div className="card-panel stack">
          {inv.person && (
            <div
              style={{ width: 96, height: 96, borderRadius: '50%', margin: '0 auto', overflow: 'hidden', background: 'var(--p0)', display: 'grid', placeItems: 'center', fontFamily: 'var(--font-serif)', fontSize: 34 }}
            >
              {inv.person.hasPortrait ? (
                <img src={`/api/invites/${token}/portrait`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                inv.person.name[0]
              )}
            </div>
          )}
          <p className="eyebrow">{t.invitedYou(inv.inviter)}</p>
          <h1 style={{ fontSize: 34 }}>{inv.treeName}</h1>
          {inv.person && <p className="muted">{t.as(inv.person.name)}</p>}
        </div>
        {err && (
          <div className="banner" role="alert">
            <strong>{err.code === 'invite_email_mismatch' ? t.wrongAccount : t.couldntJoin}</strong>
            <div>{err.message}</div>
          </div>
        )}
        {user && terms.needs ? (
          <TermsPanel />
        ) : user ? (
          <button className="btn btn-primary btn-block" disabled={accept.isPending} onClick={() => accept.mutate()}>
            {t.join}
          </button>
        ) : (
          <>
            <p className="muted">{t.signInToJoin}</p>
            <SignInPanel />
          </>
        )}
      </div>
    </div>
  );
}
